// Embed-mode detection + postMessage protocol for iframe-hosted People.
//
// Detection: `?embedded=1` query param or `window !== window.top`.
//
// Inbound (host → iframe):
//   { type: "auth",     token: "<jwt>" }                  — replaces token in localStorage
//   { type: "theme",    vars: { "--brand-500": "10 102 194", "--font-sans": "DM Sans" } }
//   { type: "navigate", path: "/t/<slug>/employees" }     — programmatic route push
//
// Outbound (iframe → host):
//   { type: "ready" }                                     — emitted on first mount
//   { type: "navigated", path: "/..." }                   — every route change
//   { type: "auth-required" }                             — 401 from API; host should re-mint a token
//
// Protocol notes:
//   - Inbound messages are accepted from any origin in dev. In production
//     the host whitelist is read from VITE_EMBED_HOST_WHITELIST (comma-
//     separated origin strings). Unmatched origins are ignored.
//   - `theme.vars` keys must be valid CSS variable names (start with `--`).
//     Values are written verbatim to :root style.

import { useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"

export function isEmbedded(): boolean {
  if (typeof window === "undefined") return false
  if (new URLSearchParams(window.location.search).get("embedded") === "1") return true
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin frame — accessing window.top throws. We're embedded.
    return true
  }
}

function getOriginWhitelist(): string[] | null {
  // VITE_EMBED_HOST_WHITELIST is a build-time env. Empty/undefined → allow all.
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_EMBED_HOST_WHITELIST
  if (!raw) return null
  return raw.split(",").map((s) => s.trim()).filter(Boolean)
}

function originAllowed(origin: string): boolean {
  const whitelist = getOriginWhitelist()
  if (!whitelist) return true
  return whitelist.includes(origin)
}

function postToParent(msg: unknown): void {
  if (typeof window === "undefined" || window.parent === window) return
  // Targets any origin — the parent window is the only listener.
  window.parent.postMessage(msg, "*")
}

interface AuthMessage  { type: "auth"; token: string }
interface ThemeMessage { type: "theme"; vars: Record<string, string> }
interface NavMessage   { type: "navigate"; path: string }
type InboundMessage = AuthMessage | ThemeMessage | NavMessage

/**
 * Wires the iframe ↔ host postMessage protocol. Call once at the app
 * shell. Safe to call when not embedded (becomes a no-op).
 */
export function useEmbedBridge(): void {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isEmbedded()) return
    document.body.classList.add("embedded")

    function handleMessage(ev: MessageEvent): void {
      if (!originAllowed(ev.origin)) return
      const data = ev.data as InboundMessage | undefined
      if (!data || typeof data !== "object" || !("type" in data)) return

      if (data.type === "auth" && typeof data.token === "string") {
        localStorage.setItem("people.token", data.token)
        return
      }
      if (data.type === "theme" && data.vars && typeof data.vars === "object") {
        for (const [key, value] of Object.entries(data.vars)) {
          if (!key.startsWith("--")) continue
          if (typeof value !== "string") continue
          document.documentElement.style.setProperty(key, value)
        }
        return
      }
      if (data.type === "navigate" && typeof data.path === "string") {
        navigate(data.path)
        return
      }
    }

    window.addEventListener("message", handleMessage)
    // Tell the host we're up + ready to receive auth/theme.
    postToParent({ type: "ready" })

    return () => {
      window.removeEventListener("message", handleMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Emit navigated on route change — host can sync URL bar / breadcrumb.
  useEffect(() => {
    if (!isEmbedded()) return
    postToParent({ type: "navigated", path: location.pathname + location.search })
  }, [location])
}

/** Notify the host that auth is required. Used by api.ts on 401. */
export function notifyAuthRequired(): void {
  if (isEmbedded()) postToParent({ type: "auth-required" })
}
